import {
  Dialog,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { SideNav } from "components";
import {
  Icons,
  LargeButton,
  MainBody,
  OverlayLoader,
  PageTitle,
  Ptag,
} from "components/core";
import React, { Fragment, useEffect, useMemo, useState } from "react";
import {
  CutoffDateDivider,
  DownloadLink,
  FileDownloadLink,
  StatusWrapper,
} from "../styles";
import UploadFile from "components/UploadFile";
import http from "services/httpService";
import DatefieldWithIcon from "components/core/dateFieldWithIcon";
import { ALL_PRODUCTS } from "constants";
import { formatDate, getFileNameFromUrl } from "utlis/utils";
import { useNavigate, useLocation } from "react-router";
import routes from "components/routes";
import SuccessAnimation from "assets/lottie/success.json";
import Lottie from "react-lottie";
import moment from "moment";
import NewDateField from "components/core/NewDateField";

const HEADERS = ["Product Name", "File Name", "File Status", "Error", " "];
const intervalTime = 45 * 1000;

const defaultOptions = {
  loop: true,
  autoplay: true,
  animationSpeed: 0.1,
  animationData: SuccessAnimation,
  rendererSettings: {
    preserveAspectRatio: "xMidYMid slice",
  },
};

const ProductStatus = () => {
  const superTaskId = localStorage.getItem("super_task_id");
  const flexProductsIds = JSON.parse(localStorage.getItem("flex_userplan_ids"));

  const { state } = useLocation();

  const [loading, setLoading] = useState(true);
  const [asyncTasks, setAsyncTasks] = useState();
  const [showUpload, setShowUpload] = useState(false);
  const [showFileUploadSuccess, setShowFileUploadSuccess] = useState(false);
  const [uploadedDocIds, setUploadedDocIds] = useState({});
  const [cutoffDate, setCutoffDate] = useState();
  const [error, setError] = useState();
  const [filesName, setFilesName] = useState({});
  const [productsPriority, setProductsPriority] = useState();

  const navigate = useNavigate();

  useEffect(() => {
    const interval = setInterval(async () => {
      await fetchProductStatus();
    }, intervalTime);
    (async function () {
      await getProductList();
      await fetchProductStatus();
    })();
    return () => clearInterval(interval);
  }, []);

  const products = useMemo(() => {
    if (!asyncTasks) return [];

    let employeeProductPlans = {};
    for (const item of asyncTasks) {
      if (item.task_type.startsWith("employee_product")) {
        const userplan = item.userplan;
        if (userplan && !(userplan in employeeProductPlans)) {
          employeeProductPlans[userplan] = item;
        }
      }
    }

    const employeeProductPlansArray = Object.values(employeeProductPlans);

    const sorted = employeeProductPlansArray.sort((a, b) => {
      const aProductKey = Object.keys(flexProductsIds).find(
        (key) => flexProductsIds[key] === a.userplan
      );
      const bProductKey = Object.keys(flexProductsIds).find(
        (key) => flexProductsIds[key] === b.userplan
      );

      return productsPriority.indexOf(aProductKey) >
        productsPriority.indexOf(bProductKey)
        ? 1
        : -1;
    });

    // convert array to object
    employeeProductPlans = {};
    for (const item of sorted) {
      employeeProductPlans[item.userplan] = item;
    }
    return employeeProductPlans;
  }, [asyncTasks]);

  const fetchProductStatus = async () => {
    const super_task_id = localStorage.getItem("super_task_id");
    const payload = {
      params: {
        super_task_id,
      },
    };
    const { data } = await http.get("business/get_taskset_details/", payload);
    setAsyncTasks(data.task_set_data.async_tasks);
    setLoading(false);
  };

  const getProductList = async () => {
    const { data } = await http.get("business/get_all_flex_products/");
    setProductsPriority(Object.keys(data));
  };

  const renderFileName = (userplanId) => {
    if (filesName[userplanId]) {
      return <Ptag fontSize="14px">{filesName[userplanId]}</Ptag>;
    }
  };

  const fileName = (product) => {
    if (isFileUploaded(product.userplan)) {
      return renderFileName(product.userplan);
    }
    return (
      <FileDownloadLink
        onClick={() => window.open(product.input_file_link, "_blank")}
      >
        {getFileNameFromUrl(product.input_file_link)}
      </FileDownloadLink>
    );
  };

  const isFileUploaded = (userplanId) => {
    return filesName[userplanId] ? true : false;
  };

  const getBgColor = (status) => {
    switch (status) {
      case "Pending":
        return "#F78500";
      case "Success":
        return "#6ACF44";
      case "Failure":
        return "#D3301A";
      case "Submitted":
        return "#F7B500";
      default:
        return "#fff";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "Pending":
        return "Processing";
      default:
        return status;
    }
  };

  const renderError = (product) => {
    let error = "-";

    if (product.error_file_path) {
      error = (
        <DownloadLink href={product.error_file_path}>Error File</DownloadLink>
      );
    } else if (product.error) {
      error = (
        <Ptag fontSize="14px" style={{ maxWidth: "40ch" }} color="#d3301a">
          {product.error}
        </Ptag>
      );
    }

    if (isFileUploaded(product.userplan)) {
      error = "-";
    }

    return error;
  };

  const renderFileStatus = (product) => {
    if (isFileUploaded(product.userplan)) {
      return (
        <StatusWrapper color={getBgColor("Submitted")}>
          <div></div>
          <Ptag fontSize="14px">{getStatusText("Submitted")}</Ptag>
        </StatusWrapper>
      );
    }
    return (
      <StatusWrapper color={getBgColor(product.status)}>
        <div></div>
        <Ptag fontSize="14px">{getStatusText(product.status)}</Ptag>
      </StatusWrapper>
    );
  };

  const productStatusRows = useMemo(() => {
    return Object.keys(products).map((userplanId) => {
      const productKey = Object.keys(flexProductsIds).find(
        (key) => flexProductsIds[key] === userplanId
      );
      const product = products[userplanId];
      const productName = ALL_PRODUCTS[productKey];

      return (
        <TableRow key={userplanId}>
          <TableCell component="th" scope="row">
            <Ptag fontSize="14px">{productName}</Ptag>
          </TableCell>
          <TableCell>{fileName(product)}</TableCell>
          <TableCell>{renderFileStatus(product)}</TableCell>
          <TableCell>{renderError(product)}</TableCell>
          <TableCell>
            {(product.error_file_path || product.error) && (
              <LargeButton
                fontSize="14px"
                label="Upload Again"
                style={{ height: "40px" }}
                onClick={() => setShowUpload(product.userplan)}
              />
            )}
          </TableCell>
        </TableRow>
      );
    });
  }, [products, filesName]);

  const productsStatus = useMemo(() => {
    if (!asyncTasks) return [];
    return asyncTasks.filter((task) =>
      task.task_type.includes("employee_product")
    );
  }, [asyncTasks]);

  const handleProductSelect = (userplanId, id) => {
    setUploadedDocIds((prevState) => ({
      ...prevState,
      [userplanId]: id,
    }));
  };

  const handleFileUpload = (userplanId, file) => {
    setFilesName((prevState) => ({
      ...prevState,
      [userplanId]: file.name,
    }));
  };

  const onDownloadTemplate = async (userplanId) => {
    if (!userplanId) return;
    const payload = {
      plan_id: userplanId,
    };
    const { data } = await http.post(
      "business/product_employee_sheet_skeleton/",
      payload,
      { responseType: "blob" }
    );
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(new Blob([data]));
    link.download = "employee_sheet.xlsx";

    // Append link to the DOM and click it
    document.body.appendChild(link);
    link.click();

    // Clean up
    document.body.removeChild(link);
    window.URL.revokeObjectURL(link.href);
  };

  const handleDateChange = (value) => {
    let newDate = value;
    if (!cutoffDate) {
      setCutoffDate(newDate);
      return;
    }
    let prevDateLen = cutoffDate.length;
    let newDateLen = value.length;
    let newDob = newDate;

    if (newDateLen < prevDateLen) {
      newDob = cutoffDate.substr(0, prevDateLen - 1);
    } else if (newDateLen <= 10) {
      if (newDateLen === 2 || newDateLen === 5) {
        newDob = newDate + "/";
      }
    } else {
      newDob = newDate.slice(0, 10);
    }
    setCutoffDate(newDob);
  };

  const showCutoffDate = useMemo(() => {
    return Object.values(products).every(
      (product) => product.status === "Success"
    );
  }, [products]);

  const isDisabled = useMemo(() => {
    // enabling if files are needed to be validated
    const doesFilesNeedsToBeUploaded = Object.keys(uploadedDocIds).length > 0;
    if (doesFilesNeedsToBeUploaded) return false;

    // enabling if cutoff date is visible
    if (showCutoffDate) return false;

    return true;
  }, [productsStatus, uploadedDocIds]);

  const updateHandler = async () => {
    if (showCutoffDate) {
      console.log(showCutoffDate, cutoffDate)
      if (!cutoffDate) {
        setError("Required *");
        return;
      }
      const isValid = moment(cutoffDate, "YYYY-MM-DD", true).isValid();
      const isFuture = moment(cutoffDate, "YYYY-MM-DD").isAfter();
      if (!isValid) {
        setError("Please enter a valid date");
        return;
      }
      if (!isFuture) {
        setError("Please enter a future date");
        return;
      }
      await updateCutoffDate();
      // navigate with state
      navigate({
        pathname: `/${routes.FLEX_PENDING_STATE}`,
        state: { cutoff_date: true, flow: state && state.flow },
      });
    } else if (Object.keys(uploadedDocIds).length) {
      setFilesName({});
      setUploadedDocIds({});
      setLoading(true);
      await updateEmployeeSheets();
    }
  };

  const updateEmployeeSheets = async () => {
    const flow = sessionStorage.getItem("flex_setup_type");
    const payload = {
      upload_flow: flow ? flow : "setup",
      product_employee_sheet_mapping: uploadedDocIds,
      super_task_id: superTaskId,
      page: "product_employee",
    };
    try {
      // setIsLoading(true);
      const { data } = await http.post(
        "business/employee_data_upload_pre_issuance/",
        payload
      );
      setTimeout(() => {
        // setIsLoading(false);
        window.location.reload();
      }, 3000);
    } catch (ex) {
      alert("Something went wrong");
    }
  };

  const updateCutoffDate = async () => {
    const flow = sessionStorage.getItem("flex_setup_type");
    const payload = {
      super_task_id: superTaskId,
      upload_flow: flow ? flow : "setup",
      cutoff_date: formatDate(cutoffDate),
      page: "cutoff",
    };
    try {
      setLoading(true);
      const { data } = await http.post(
        "business/employee_data_upload_pre_issuance/",
        payload
      );
      navigate(`/${routes.FLEX_DASHBOARD}`);
    } catch (ex) {
      alert("Something went wrong");
      console.log(ex.response.data, "at parent components");
    } finally {
      setLoading(false);
    }
  };

  const getProductName = (userplanId) => {
    return ALL_PRODUCTS[
      Object.keys(flexProductsIds).find(
        (id) => flexProductsIds[id] === userplanId
      )
    ];
  };

  const handleUpload = () => {
    setShowUpload(false);
    setShowFileUploadSuccess(true);
    setTimeout(() => {
      setShowFileUploadSuccess(false);
    }, 2400);
  };

  return (
    <>
      {loading ? (
        <OverlayLoader />
      ) : (
        <>
          <SideNav />
          <MainBody
            style={{
              minHeight: "100vh",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            <div>
              <PageTitle title="Upload Employee Data" />
              <TableContainer
                sx={{
                  fontSize: "100% !important",
                }}
              >
                <Table
                  sx={{
                    minWidth: 650,
                  }}
                >
                  <TableHead>
                    <TableRow>
                      {HEADERS.map((header) => (
                        <TableCell>
                          <Ptag fontSize="16px" fontWeight="700">
                            {header}
                          </Ptag>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>{productStatusRows}</TableBody>
                </Table>
              </TableContainer>

              {showCutoffDate && (
                <div>
                  <CutoffDateDivider />
                  <Ptag
                    fontSize="20px"
                    fontWeight="600"
                    marginBottom="20px"
                    marginTop="50px"
                  >
                    Set Cut-off Date for Data Collection
                  </Ptag>
                  {/* <DatefieldWithIcon
                    hideIcon
                    value={cutoffDate}
                    placeholder="Cutoff Date (DD/MM/YYYY)"
                    error={error}
                    onChange={(e) => handleDateChange(e.target.value)}
                    required
                  ></DatefieldWithIcon> */}
                  <NewDateField defaultValue={cutoffDate} error={error} setDate={(value)=>setCutoffDate(value)}/>
                </div>
              )}
            </div>
            <LargeButton
              label={showCutoffDate ? "Continue" : "Verify"}
              disabled={isDisabled}
              onClick={updateHandler}
              style={{
                opacity: isDisabled ? 0.5 : 1,
                padding: "1.8rem 2rem",
              }}
            />
          </MainBody>
          <Dialog
            open={showUpload}
            onClose={() => setShowUpload(false)}
            fullWidth
            maxWidth="md"
            PaperProps={{ sx: { borderRadius: "20px", marginLeft: "252px" } }}
          >
            <section
              style={{
                padding: "6rem 8rem",
              }}
            >
              <PageTitle title={getProductName(showUpload)} />
              <UploadFile
                text="Upload Excel"
                label="Employee Sheet"
                getFile={(file) => handleFileUpload(showUpload, file)}
                sampleFile="FlexEmployeeSheet"
                productType="ghi"
                onSelect={(id) => handleProductSelect(showUpload, id)}
                format="excel"
                type="employees_excel"
                onDownload={() => onDownloadTemplate(showUpload)}
              />
              <LargeButton
                style={{ marginTop: "5rem" }}
                label="Upload File"
                onClick={handleUpload}
              />
            </section>
          </Dialog>
          <Dialog
            open={showFileUploadSuccess}
            onClose={() => setShowFileUploadSuccess(false)}
            fullWidth
            maxWidth="sm"
            PaperProps={{ sx: { borderRadius: "20px", marginLeft: "252px" } }}
          >
            <section
              style={{
                padding: "6rem 8rem",
              }}
            >
              <Lottie
                options={defaultOptions}
                height={140}
                width={140}
                isClickToPauseDisabled
              />
              <Ptag fontSize="20px" textAlign="center" marginTop="24px">
                File Uploaded Successfully
              </Ptag>
            </section>
          </Dialog>
        </>
      )}
    </>
  );
};

export default ProductStatus;
